import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import {
  useAutoTaggingPlanTypes,
  type PlanTypesSource,
} from "../../../hooks/useAutoTaggingPlanTypes";
import {
  useConfigurableConcepts,
  type ConceptFetcher,
} from "../../../hooks/useConfigurableConcepts";
import { usePaginatedConceptValues } from "../../../hooks/usePaginatedConceptValues";
import {
  getConceptDisplayLabel,
} from "../../../services/autoTaggingService";
import {
  getConceptsValuesSearch,
  type ConceptValue,
} from "../../../services/regulationsService";
import { useBrandThemeColors } from "../../../branding";
import { CONCEPT_VALUE_ALL_OPTION } from "../constants";
import {
  parseObjectSubTypeCodes,
  serializeObjectSubTypeCodes,
} from "./objectSubTypeCodes";

const CASCADE_COLUMN_HEIGHT = 220;
/** Idle delay before Dimension filter + Value probe run (input stays live). */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Keep the text field responsive; apply the query after the user pauses typing.
 * Empty / whitespace-only input applies immediately so Clear feels instant.
 */
function useDebouncedSearchQuery(searchInput: string): string {
  const [searchQuery, setSearchQuery] = useState(searchInput);
  useEffect(() => {
    if (!searchInput.trim()) {
      setSearchQuery("");
      return;
    }
    const timerId = window.setTimeout(() => {
      setSearchQuery(searchInput);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timerId);
  }, [searchInput]);
  return searchQuery;
}

function conceptCodesEqual(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** Case-insensitive membership for Dimension codes in Value-hit sets. */
function dimensionHitSetHas(
  hits: ReadonlySet<string>,
  conceptCode: string,
): boolean {
  const lower = conceptCode.trim().toLowerCase();
  if (!lower) return false;
  if (hits.has(conceptCode) || hits.has(lower)) return true;
  for (const hit of hits) {
    if (hit.trim().toLowerCase() === lower) return true;
  }
  return false;
}

const cascadeColumnScrollSx = {
  height: CASCADE_COLUMN_HEIGHT,
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  scrollbarWidth: "thin",
  scrollbarColor: "#C4C4C4 transparent",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#C4C4C4",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
} as const;

const formFieldLabelSx = {
  fontSize: "0.875rem",
  fontWeight: 500,
  color: "#262626",
  mb: 0.75,
};

/** One Reference Dimension with its selected Values (multi-dimension cascade). */
export type ReferenceDimensionSelection = {
  conceptCode: string;
  conceptDisplayName: string;
  values: ConceptValue[];
  allValues: boolean;
};

export type ReferenceCascadeSelection = {
  objectType: string;
  /**
   * Selected Reference Dimension value codes (`a,b` or `__all__`), stored in
   * the legacy `objectSubType` form field for submit/hydrate.
   * Mirrors the first entry of `referenceDimensions` for OTO / Enhanced.
   */
  objectSubType: string;
  /**
   * Optional display labels used before plan-types finish loading
   * (e.g. edit dialog hydrated from list row names).
   */
  objectTypeName?: string;
  objectSubTypeName?: string;
  /**
   * Selected Reference Dimension concept (`kind=reference`).
   * Empty when Object-only (no Dimension/Value).
   * Mirrors the first entry of `referenceDimensions`.
   */
  referenceConceptCode?: string;
  /** Display label for `referenceConceptCode` (from `display_name`). */
  referenceConceptDisplayName?: string;
  /**
   * All selected Reference Dimensions (may be empty = object-only).
   * Classification submit uses this full list as `reference_items`.
   */
  referenceDimensions?: ReferenceDimensionSelection[];
  /**
   * Impacted Items fields echoed through when Reference is embedded in a
   * combined draft value (Add page). Reference confirm must not overwrite them.
   */
  conceptCode: string;
  conceptValues: ConceptValue[];
  allValues: boolean;
};

export type ImpactedCascadeSelection = {
  conceptCode: string;
  conceptValues: ConceptValue[];
  allValues: boolean;
};

export function formatReferenceItemsLabel(
  objectName: string,
  dimensionLabel: string,
  values: ConceptValue[],
  allValues = false,
  separator = "·",
): string {
  const objectPart = objectName.trim();
  const dimensionPart = dimensionLabel.trim();
  if (!objectPart || !dimensionPart) return "";
  const head = `${objectPart}${separator}${dimensionPart}`;
  if (allValues) {
    return `${head} = ${CONCEPT_VALUE_ALL_OPTION.name}`;
  }
  const valuePart = values
    .map((item) => item.name?.trim() || item.code)
    .filter(Boolean)
    .join(", ");
  if (!valuePart) return head;
  return `${head} = ${valuePart}`;
}

/** Join multi-dimension Reference labels: `Obj·Dim1 = A; Obj·Dim2 = B`. */
export function formatMultiReferenceItemsLabel(
  objectName: string,
  dimensions: readonly ReferenceDimensionSelection[],
  separator = "·",
): string {
  const objectPart = objectName.trim();
  if (!objectPart) return "";
  if (dimensions.length === 0) return objectPart;
  return dimensions
    .map((dim) =>
      formatReferenceItemsLabel(
        objectPart,
        dim.conceptDisplayName || dim.conceptCode,
        dim.allValues ? [] : dim.values,
        dim.allValues,
        separator,
      ),
    )
    .filter(Boolean)
    .join("; ");
}

/** Prefer `referenceDimensions`; fall back to legacy single-dimension fields. */
export function normalizeReferenceDimensions(
  value: Pick<
    ReferenceCascadeSelection,
    | "referenceDimensions"
    | "objectSubType"
    | "objectSubTypeName"
    | "referenceConceptCode"
    | "referenceConceptDisplayName"
  >,
): ReferenceDimensionSelection[] {
  if (value.referenceDimensions && value.referenceDimensions.length > 0) {
    return value.referenceDimensions.map((dim) => ({
      conceptCode: dim.conceptCode,
      conceptDisplayName: dim.conceptDisplayName,
      values: [...dim.values],
      allValues: Boolean(dim.allValues),
    }));
  }

  const conceptCode = (value.referenceConceptCode ?? "").trim();
  const objectSubType = (value.objectSubType ?? "").trim();
  if (!conceptCode || !objectSubType) return [];

  const codes = parseObjectSubTypeCodes(objectSubType);
  const isAll =
    codes.length === 1 &&
    codes[0]!.toLowerCase() === CONCEPT_VALUE_ALL_OPTION.code.toLowerCase();
  const names = (value.objectSubTypeName ?? "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  return [
    {
      conceptCode,
      conceptDisplayName:
        value.referenceConceptDisplayName?.trim() || conceptCode,
      allValues: isAll,
      values: isAll
        ? []
        : codes.map((code, index) => ({
            id: code,
            code,
            name: names[index] || code,
          })),
    },
  ];
}

/** Mirror first dimension onto legacy flat fields (OTO / Enhanced consumers). */
export function legacyFieldsFromReferenceDimensions(
  dimensions: readonly ReferenceDimensionSelection[],
): Pick<
  ReferenceCascadeSelection,
  | "objectSubType"
  | "objectSubTypeName"
  | "referenceConceptCode"
  | "referenceConceptDisplayName"
> {
  const first = dimensions[0];
  if (!first) {
    return {
      objectSubType: "",
      objectSubTypeName: "",
      referenceConceptCode: undefined,
      referenceConceptDisplayName: undefined,
    };
  }
  if (first.allValues) {
    return {
      objectSubType: CONCEPT_VALUE_ALL_OPTION.code,
      objectSubTypeName: CONCEPT_VALUE_ALL_OPTION.name,
      referenceConceptCode: first.conceptCode,
      referenceConceptDisplayName: first.conceptDisplayName,
    };
  }
  const codes = first.values.map((item) => item.code);
  return {
    objectSubType: serializeObjectSubTypeCodes(codes, false),
    objectSubTypeName: first.values
      .map((item) => item.name?.trim() || item.code)
      .join(", "),
    referenceConceptCode: first.conceptCode,
    referenceConceptDisplayName: first.conceptDisplayName,
  };
}

export {
  parseObjectSubTypeCodes,
  serializeObjectSubTypeCodes,
} from "./objectSubTypeCodes";

function normalizeConceptValueCode(code: string): string {
  return code.trim().toLowerCase();
}

function normalizeConceptValueLabel(item: ConceptValue): string {
  return (item.name || item.code).trim().toLowerCase();
}

/** Match by code, or by display name when edit hydrate codes differ from search. */
function conceptValuesMatch(left: ConceptValue, right: ConceptValue): boolean {
  const leftCode = normalizeConceptValueCode(left.code);
  const rightCode = normalizeConceptValueCode(right.code);
  if (leftCode && rightCode && leftCode === rightCode) return true;
  const leftName = normalizeConceptValueLabel(left);
  const rightName = normalizeConceptValueLabel(right);
  return Boolean(leftName && rightName && leftName === rightName);
}

/**
 * Keep selected values first (draft order), then the remaining page options.
 * Off-page selections are pinned until their page loads. Dedupes by code and
 * by display name so edit-hydrate rows do not appear twice when the search
 * API returns a different code for the same label.
 */
export function mergeValuesWithSelected(
  options: ConceptValue[],
  selected: ConceptValue[],
): ConceptValue[] {
  const uniqueSelected: ConceptValue[] = [];
  const selectedCodes = new Set<string>();
  const selectedNames = new Set<string>();
  for (const item of selected) {
    const code = normalizeConceptValueCode(item.code);
    const name = normalizeConceptValueLabel(item);
    if (code && selectedCodes.has(code)) continue;
    if (name && selectedNames.has(name)) continue;
    if (code) selectedCodes.add(code);
    if (name) selectedNames.add(name);
    uniqueSelected.push(item);
  }

  const claimedIndexes = new Set<number>();
  const pinned: ConceptValue[] = [];
  for (const sel of uniqueSelected) {
    const matchIndex = options.findIndex((opt, index) => {
      if (claimedIndexes.has(index)) return false;
      return conceptValuesMatch(sel, opt);
    });
    if (matchIndex >= 0) {
      claimedIndexes.add(matchIndex);
      pinned.push(options[matchIndex]!);
    } else {
      pinned.push(sel);
    }
  }

  const seenCodes = new Set(
    pinned.map((item) => normalizeConceptValueCode(item.code)).filter(Boolean),
  );
  const seenNames = new Set(
    pinned.map((item) => normalizeConceptValueLabel(item)).filter(Boolean),
  );
  const unselected: ConceptValue[] = [];
  options.forEach((item, index) => {
    if (claimedIndexes.has(index)) return;
    const code = normalizeConceptValueCode(item.code);
    const name = normalizeConceptValueLabel(item);
    if (code && seenCodes.has(code)) return;
    if (name && seenNames.has(name)) return;
    if (code) seenCodes.add(code);
    if (name) seenNames.add(name);
    unselected.push(item);
  });

  return [...pinned, ...unselected];
}

/** Trigger / table label: `Topic = A, B` (or `Topic = All`). */
export function formatImpactedItemsLabel(
  dimensionLabel: string,
  values: ConceptValue[],
  allValues = false,
): string {
  const dimensionPart = dimensionLabel.trim();
  if (!dimensionPart) return "";
  if (allValues) return `${dimensionPart} = ${CONCEPT_VALUE_ALL_OPTION.name}`;
  const names = values
    .map((item) => item.name?.trim() || item.code)
    .filter(Boolean);
  if (names.length === 0) return dimensionPart;
  return `${dimensionPart} = ${names.join(", ")}`;
}

/** Concept V3 cascade column header (Object / Dimension / Value). */
const cascadeColumnHeaderSx = {
  px: "18px",
  pt: 0,
  pb: "10px",
  fontSize: "0.875rem",
  fontWeight: 500,
  color: "#0D0D0D",
  bgcolor: "#fff",
  flexShrink: 0,
} as const;

const CASCADE_OPTION_HOVER_BG = "#F3F6FE";
const CASCADE_OPTION_SELECTED_BG = "#F3F6FE";

type ColumnRowProps = Readonly<{
  label: string;
  selected: boolean;
  onClick: () => void;
  showChevron?: boolean;
  /** Object column in Concept V3 shows a checkmark next to the label when selected. */
  showCheck?: boolean;
  /** Optional count pill (Dimension rows). */
  count?: number;
}>;

function ColumnRow({
  label,
  selected,
  onClick,
  showChevron = true,
  showCheck = false,
  count,
}: ColumnRowProps) {
  const { primaryColor } = useBrandThemeColors();
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        width: "100%",
        minHeight: 44,
        border: "none",
        boxShadow: selected ? `inset 3px 0 0 ${primaryColor}` : "none",
        textAlign: "left",
        px: 2,
        py: 1.5,
        cursor: "pointer",
        bgcolor: selected ? CASCADE_OPTION_SELECTED_BG : "transparent",
        color: selected ? primaryColor : "#0D0D0D",
        fontSize: "0.875rem",
        fontWeight: 400,
        fontFamily: "inherit",
        transition: "background-color 0.12s ease",
        "&:hover": {
          bgcolor: CASCADE_OPTION_HOVER_BG,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          minWidth: 0,
          flex: 1,
        }}
      >
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Box>
        {showCheck && selected ? (
          <CheckIcon
            sx={{ fontSize: 14, color: primaryColor, flexShrink: 0 }}
          />
        ) : null}
        {typeof count === "number" && count > 0 ? (
          <Box
            component="span"
            sx={{
              ml: "auto",
              minWidth: 36,
              height: 20,
              px: 1.25,
              boxSizing: "border-box",
              borderRadius: 999,
              bgcolor: "rgba(41,94,241,0.08)",
              color: primaryColor,
              fontSize: "0.75rem",
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {count}
          </Box>
        ) : null}
      </Box>
      {showChevron ? (
        <ChevronRightIcon
          sx={{
            fontSize: 14,
            color: "currentColor",
            flexShrink: 0,
            opacity: 0.85,
          }}
        />
      ) : null}
    </Box>
  );
}

type ValueOptionRowProps = Readonly<{
  label: string;
  checked: boolean;
  onClick: () => void;
}>;

/** Concept V3 Value option: 16×16 rounded checkbox + label. */
function ValueOptionRow({ label, checked, onClick }: ValueOptionRowProps) {
  const { primaryColor } = useBrandThemeColors();
  return (
    <Box
      component="button"
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        width: "100%",
        minHeight: 44,
        border: "none",
        bgcolor: "transparent",
        px: "18px",
        py: 1.5,
        cursor: "pointer",
        textAlign: "left",
        fontFamily: "inherit",
        "&:hover": { bgcolor: "#F9FAFB" },
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 16,
          height: 16,
          borderRadius: "3px",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: checked
            ? `1.5px solid ${primaryColor}`
            : "1.5px solid #D1D5DC",
          bgcolor: checked ? primaryColor : "#fff",
          boxSizing: "border-box",
        }}
      >
        {checked ? (
          <CheckIcon sx={{ fontSize: 11, color: "#fff" }} />
        ) : null}
      </Box>
      <Typography
        sx={{
          fontSize: "0.875rem",
          color: "#0D0D0D",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

type TriggerFieldProps = Readonly<{
  label: string;
  required?: boolean;
  displayValue: string;
  placeholder: string;
  open: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  onClick: (event: MouseEvent<HTMLElement>) => void;
}>;

function CascadeTriggerField({
  label,
  required,
  displayValue,
  placeholder,
  open,
  error,
  helperText,
  disabled,
  onClick,
}: TriggerFieldProps) {
  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Typography sx={formFieldLabelSx}>
        {label}
        {required ? (
          <Box component="span" sx={{ color: "error.main", ml: 0.25 }}>
            *
          </Box>
        ) : null}
      </Typography>
      <Button
        fullWidth
        variant="outlined"
        color="inherit"
        className="cascade-trigger-field"
        disabled={disabled}
        onClick={onClick}
        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />}
        aria-haspopup="dialog"
        aria-expanded={open}
        sx={{
          justifyContent: "space-between",
          height: 40,
          px: 1.5,
          borderRadius: "6px",
          borderColor: error ? "error.main" : "#D1D5DB",
          color: `${displayValue ? "#262626" : "#9CA3AF"} !important`,
          textTransform: "none",
          fontWeight: 400,
          bgcolor: "#fff",
          "&:hover": {
            borderColor: error ? "error.main" : "#9CA3AF",
            bgcolor: "#F9FAFB",
            color: `${displayValue ? "#262626" : "#9CA3AF"} !important`,
          },
          "&.Mui-disabled": {
            borderColor: "#E5E7EB",
            color: `${displayValue ? "#262626" : "#9CA3AF"} !important`,
            bgcolor: "#fff",
            opacity: 1,
            WebkitTextFillColor: displayValue ? "#262626" : "#9CA3AF",
          },
          "& .MuiButton-endIcon": { ml: 1 },
          "& .MuiButton-endIcon .MuiSvgIcon-root": {
            color: "#9CA3AF !important",
          },
        }}
      >
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "left",
            flex: 1,
          }}
        >
          {displayValue || placeholder}
        </Box>
      </Button>
      {/*
        Keep helper slot in normal flow under this field only. Parent cascade
        grids must use alignItems:start so a message here does not sink the
        sibling Reference/Impacted trigger.
      */}
      {helperText ? (
        <FormHelperText error={error} sx={{ mx: 0, mt: 0.5, mb: 0 }}>
          {helperText}
        </FormHelperText>
      ) : null}
    </Box>
  );
}

type ReferenceItemsCascadePickerProps = Readonly<{
  value: ReferenceCascadeSelection;
  onChange: (next: ReferenceCascadeSelection) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  /** Defaults to "Reference Items". */
  label?: string;
  /** Defaults to "Please select reference Items". */
  placeholder?: string;
  /** Object type codes to hide from the Object column. */
  excludedObjectTypes?: readonly string[];
  /**
   * Joins object and dimension in the closed field.
   * Conditional Classification uses ":". Other screens keep the middle dot.
   */
  labelSeparator?: string;
  /**
   * When true (default), only types with `configurable !== false` are listed.
   * OTO passes `false` — object list comes from assessment-plans catalog.
   */
  configurableOnly?: boolean;
  /**
   * Classification → `auto-tagging` (default).
   * OTO Reference → `assessment-plans-reference`;
   * OTO Impact → `assessment-plans-impacted`
   * (object types from `/assessment-plans/configurable-concepts`).
   */
  typesSource?: PlanTypesSource;
  /**
   * Override Dimension concept fetch after Object is selected.
   * Classification default: `/auto-tagging/plans/configurable-concepts?kind=reference`.
   * OTO: `/assessment-plans/configurable-concepts` (per Reference / Impact side).
   */
  fetchConcepts?: ConceptFetcher;
}>;

export function ReferenceItemsCascadePicker({
  value,
  onChange,
  error,
  helperText,
  disabled,
  label = "Reference Items",
  placeholder = "Please select reference Items",
  excludedObjectTypes = [],
  configurableOnly = true,
  labelSeparator = "·",
  typesSource = "auto-tagging",
  fetchConcepts,
}: ReferenceItemsCascadePickerProps) {
  const { primaryColor } = useBrandThemeColors();
  /** Bumped on every popover open so types/concepts refetch for freshness. */
  const [dataReloadToken, setDataReloadToken] = useState(0);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedSearchQuery(searchInput);
  const [draftObjectType, setDraftObjectType] = useState(value.objectType);
  /** Active Dimension in the Value column (does not clear other Dimension picks). */
  const [draftActiveConcept, setDraftActiveConcept] = useState(
    () => normalizeReferenceDimensions(value)[0]?.conceptCode ?? "",
  );

  type DraftDimState = {
    values: ConceptValue[];
    allValues: boolean;
  };

  /** Selections keyed by Dimension concept code — multi-Dimension simultaneous pick. */
  const [draftSelections, setDraftSelections] = useState<
    Record<string, DraftDimState>
  >(() => {
    const next: Record<string, DraftDimState> = {};
    for (const dim of normalizeReferenceDimensions(value)) {
      next[dim.conceptCode] = {
        values: [...dim.values],
        allValues: dim.allValues,
      };
    }
    return next;
  });

  const open = Boolean(anchorEl);
  const hasObject = Boolean(draftObjectType.trim());

  const excludedSet = useMemo(
    () =>
      new Set(
        excludedObjectTypes
          .map((code) => code.trim().toLowerCase())
          .filter(Boolean),
      ),
    [excludedObjectTypes],
  );

  const {
    objectTypeOptions: allObjectTypeOptions,
    loading: planTypesLoading,
  } = useAutoTaggingPlanTypes({
    configurableOnly,
    source: typesSource,
    // Fetch Object types only while the popover is open (not on Add/Edit mount).
    enabled: open,
    reloadToken: dataReloadToken,
  });

  const objectTypeOptions = useMemo(() => {
    const filtered =
      excludedSet.size === 0
        ? allObjectTypeOptions
        : allObjectTypeOptions.filter(
            (option) => !excludedSet.has(option.code.trim().toLowerCase()),
          );
    const code = draftObjectType.trim();
    if (!code) return filtered;
    const codeLower = code.toLowerCase();
    if (filtered.some((option) => option.code.trim().toLowerCase() === codeLower)) {
      return filtered;
    }
    // Keep the hydrated Object visible when /plans/types failed or filtered it out.
    return [
      {
        code,
        name: value.objectTypeName?.trim() || code,
      },
      ...filtered,
    ];
  }, [
    allObjectTypeOptions,
    draftObjectType,
    excludedSet,
    value.objectTypeName,
  ]);

  // Dimensions after Object select: Classification `kind=reference`, or OTO fetcher.
  // Load only while popover is open — not when the Add/Edit form mounts.
  const { conceptOptions: fetchedConceptOptions, loading: conceptsLoading } =
    useConfigurableConcepts(
      hasObject ? draftObjectType : null,
      fetchConcepts ?? null,
      fetchConcepts ? null : "reference",
      { enabled: open, reloadToken: dataReloadToken },
    );

  const conceptOptions = useMemo(() => {
    const selectedDims = Object.entries(draftSelections).map(
      ([conceptCode, entry]) => ({
        conceptCode,
        conceptDisplayName:
          normalizeReferenceDimensions(value).find(
            (dim) => dim.conceptCode === conceptCode,
          )?.conceptDisplayName || conceptCode,
        hasPick: entry.allValues || entry.values.length > 0,
      }),
    );
    const seen = new Set(
      fetchedConceptOptions.map((item) => item.code.trim().toLowerCase()),
    );
    const merged = [...fetchedConceptOptions];
    for (const dim of selectedDims) {
      if (!dim.hasPick) continue;
      const code = dim.conceptCode.trim();
      if (!code || seen.has(code.toLowerCase())) continue;
      seen.add(code.toLowerCase());
      merged.push({
        code,
        name: code,
        display_name: dim.conceptDisplayName.trim() || code,
      });
    }
    return merged;
  }, [draftSelections, fetchedConceptOptions, value]);

  // Values are per-Dimension concept-values (not shared plan-types subtypes).
  const {
    options: valueOptions,
    loading: valuesLoading,
    setInput: setValueSearchInput,
    loadOptions,
    loadNext,
    hasMoreNext,
    loadingMore,
  } = usePaginatedConceptValues(draftActiveConcept, 20);
  const valuesScrollRef = useRef<HTMLDivElement | null>(null);
  /** Dimensions whose values matched the latest search `name` probe. */
  const [dimensionValueHits, setDimensionValueHits] = useState<
    ReadonlySet<string>
  >(() => new Set());
  /** True while multi-Dimension Value probe is in flight. */
  const [valueSearchBusy, setValueSearchBusy] = useState(false);
  const valueSearchRequestIdRef = useRef(0);
  const activeProbeKeyRef = useRef("");
  const activeSelectedLoadKeyRef = useRef("");

  const showDimensionAndValue = hasObject;
  const activeDraft = draftSelections[draftActiveConcept] ?? {
    values: [] as ConceptValue[],
    allValues: false,
  };

  const syncDraftFromValue = useCallback(() => {
    setDraftObjectType(value.objectType);
    const dims = normalizeReferenceDimensions(value);
    const next: Record<string, DraftDimState> = {};
    for (const dim of dims) {
      next[dim.conceptCode] = {
        values: [...dim.values],
        allValues: dim.allValues,
      };
    }
    setDraftSelections(next);
    setDraftActiveConcept(dims[0]?.conceptCode ?? "");
    setSearchInput("");
    setDimensionValueHits(new Set());
  }, [value]);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    if (disabled) return;
    syncDraftFromValue();
    // Refetch Object types + Dimensions on every open (Values already force-load below).
    setDataReloadToken((token) => token + 1);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const dimensionCodesKey = useMemo(
    () =>
      conceptOptions
        .map((item) => item.code.trim())
        .filter(Boolean)
        .join(","),
    [conceptOptions],
  );

  // Probe Value hits when the debounced search query changes (not per keystroke).
  useEffect(() => {
    if (!open || !hasObject) {
      valueSearchRequestIdRef.current += 1;
      activeProbeKeyRef.current = "";
      activeSelectedLoadKeyRef.current = "";
      setValueSearchBusy(false);
      setDimensionValueHits((prev) => (prev.size === 0 ? prev : new Set()));
      return;
    }

    const trimmed = search.trim();
    if (!trimmed) {
      valueSearchRequestIdRef.current += 1;
      activeProbeKeyRef.current = "";
      setValueSearchBusy(false);
      setDimensionValueHits((prev) => (prev.size === 0 ? prev : new Set()));
      return;
    }

    const dimensionCodes = dimensionCodesKey
      ? dimensionCodesKey.split(",")
      : [];
    if (dimensionCodes.length === 0) return;

    const probeKey = `${trimmed}|${dimensionCodes.join(",")}`;
    if (activeProbeKeyRef.current === probeKey) {
      setValueSearchBusy(false);
      return;
    }
    activeProbeKeyRef.current = probeKey;
    setDimensionValueHits((prev) => (prev.size === 0 ? prev : new Set()));
    setValueSearchBusy(true);
    const requestId = ++valueSearchRequestIdRef.current;

    void (async () => {
      try {
        const hitResults = await Promise.all(
          dimensionCodes.map(async (code) => {
            const response = await getConceptsValuesSearch(
              [code],
              trimmed || undefined,
              1,
              1,
            );
            const items = Array.isArray(response.data) ? response.data : [];
            return { code, hasHit: items.length > 0 };
          }),
        );

        if (requestId !== valueSearchRequestIdRef.current) return;

        const hits = new Set<string>();
        for (const result of hitResults) {
          if (result.hasHit) hits.add(result.code);
        }
        setDimensionValueHits(hits);
      } catch {
        if (requestId === valueSearchRequestIdRef.current) {
          setDimensionValueHits(new Set());
        }
      } finally {
        if (requestId === valueSearchRequestIdRef.current) {
          setValueSearchBusy(false);
        }
      }
    })();
  }, [dimensionCodesKey, hasObject, open, search]);

  const searchLower = search.trim().toLowerCase();

  const dimensionMatchesSearchByName = useCallback(
    (conceptCode: string, queryLower: string): boolean => {
      if (!queryLower) return false;
      const option = conceptOptions.find((item) =>
        conceptCodesEqual(item.code, conceptCode),
      );
      if (!option) return false;
      const label = getConceptDisplayLabel(option).toLowerCase();
      return (
        label.includes(queryLower) ||
        option.code.toLowerCase().includes(queryLower)
      );
    },
    [conceptOptions],
  );

  /**
   * Value API `name` filter:
   * - Dimension name match only → load all Values for that Dimension
   * - Value hit (or both) → filter Values by the search text
   */
  const valueSearchNameForActive = useMemo(() => {
    const trimmed = search.trim();
    if (!trimmed || !draftActiveConcept.trim()) return undefined;
    const nameMatch = dimensionMatchesSearchByName(
      draftActiveConcept,
      trimmed.toLowerCase(),
    );
    const valueHit = dimensionHitSetHas(dimensionValueHits, draftActiveConcept);
    if (nameMatch && !valueHit) return undefined;
    return trimmed;
  }, [
    dimensionMatchesSearchByName,
    dimensionValueHits,
    draftActiveConcept,
    search,
  ]);

  // Selected Dimension + search: load Values via user-search API (not client filter only).
  useEffect(() => {
    if (!open || !hasObject || !draftActiveConcept.trim()) return;

    const trimmedQuery = valueSearchNameForActive ?? "";
    const loadKey = `${draftActiveConcept}|${trimmedQuery}`;
    if (activeSelectedLoadKeyRef.current === loadKey) return;
    activeSelectedLoadKeyRef.current = loadKey;

    setValueSearchInput(trimmedQuery);
    void loadOptions(trimmedQuery || undefined, { force: true });
  }, [
    draftActiveConcept,
    hasObject,
    loadOptions,
    open,
    setValueSearchInput,
    valueSearchNameForActive,
  ]);

  /** Overlay Dimension+Value only while Object → concepts reload. */
  const showDimValueBodyLoading = hasObject && conceptsLoading;

  const objectName =
    objectTypeOptions.find(
      (item) =>
        item.code.trim().toLowerCase() === value.objectType.trim().toLowerCase(),
    )?.name?.trim() ||
    value.objectTypeName?.trim() ||
    value.objectType;

  const committedDimensions = normalizeReferenceDimensions(value);
  const displayValue = formatMultiReferenceItemsLabel(
    objectName,
    committedDimensions,
    labelSeparator,
  );

  // Search is for Dimension/Value after Object is chosen — do not filter Objects by it.
  const filteredObjects = objectTypeOptions;

  const filteredDimensions = useMemo(() => {
    if (!hasObject || !searchLower) return conceptOptions;
    return conceptOptions.filter((item) => {
      const code = item.code;
      // Name match is immediate; Value hits arrive after the probe.
      if (dimensionMatchesSearchByName(code, searchLower)) return true;
      if (dimensionHitSetHas(dimensionValueHits, code)) return true;
      return false;
    });
  }, [
    conceptOptions,
    dimensionMatchesSearchByName,
    dimensionValueHits,
    hasObject,
    searchLower,
  ]);

  // Prefer a name-matched Dimension immediately; otherwise a Value-hit Dimension.
  useEffect(() => {
    if (!open || !hasObject || !searchLower) return;
    if (conceptsLoading) return;
    if (filteredDimensions.length === 0) {
      // Still probing Values — wait before clearing the active Dimension.
      return;
    }

    const activeMatchesName = dimensionMatchesSearchByName(
      draftActiveConcept,
      searchLower,
    );
    const activeHasValueHit = dimensionHitSetHas(
      dimensionValueHits,
      draftActiveConcept,
    );
    if (activeMatchesName || activeHasValueHit) return;

    const firstNameMatch = filteredDimensions.find((item) =>
      dimensionMatchesSearchByName(item.code, searchLower),
    );
    if (firstNameMatch) {
      setDraftActiveConcept(firstNameMatch.code);
      return;
    }

    if (valueSearchBusy) return;
    const firstValueHit = filteredDimensions.find((item) =>
      dimensionHitSetHas(dimensionValueHits, item.code),
    );
    if (firstValueHit) {
      setDraftActiveConcept(firstValueHit.code);
    }
  }, [
    conceptsLoading,
    dimensionMatchesSearchByName,
    dimensionValueHits,
    draftActiveConcept,
    filteredDimensions,
    hasObject,
    open,
    searchLower,
    valueSearchBusy,
  ]);

  const mergedValueOptions = useMemo(() => {
    if (activeDraft.allValues) {
      return mergeValuesWithSelected(valueOptions, []);
    }
    // When Values are API-filtered, only pin selections that still match.
    const applySearch = Boolean(valueSearchNameForActive);
    const pinnedSelected = applySearch
      ? activeDraft.values.filter((item) =>
          (item.name || item.code)
            .toLowerCase()
            .includes(valueSearchNameForActive!.toLowerCase()),
        )
      : activeDraft.values;
    return mergeValuesWithSelected(valueOptions, pinnedSelected);
  }, [activeDraft.allValues, activeDraft.values, valueOptions, valueSearchNameForActive]);

  const handleSelectObject = (objectTypeCode: string) => {
    if (draftObjectType === objectTypeCode) return;
    setDraftObjectType(objectTypeCode);
    setDraftActiveConcept("");
    setDraftSelections({});
    setSearchInput("");
    setDimensionValueHits(new Set());
    setValueSearchBusy(false);
    activeProbeKeyRef.current = "";
    activeSelectedLoadKeyRef.current = "";
  };

  const handleSelectDimension = (conceptCode: string) => {
    setDraftActiveConcept(conceptCode);
  };

  const handleSelectAllValues = () => {
    if (!draftActiveConcept.trim()) return;
    setDraftSelections((prev) => {
      const current = prev[draftActiveConcept];
      const nextAll = !(current?.allValues ?? false);
      return {
        ...prev,
        [draftActiveConcept]: { values: [], allValues: nextAll },
      };
    });
  };

  const handleToggleValue = (option: ConceptValue) => {
    if (!draftActiveConcept.trim()) return;
    setDraftSelections((prev) => {
      const current = prev[draftActiveConcept] ?? {
        values: [],
        allValues: false,
      };
      const exists = current.values.some((item) =>
        conceptValuesMatch(item, option),
      );
      const nextValues = exists
        ? current.values.filter((item) => !conceptValuesMatch(item, option))
        : [
            ...current.values.filter(
              (item) => !conceptValuesMatch(item, option),
            ),
            option,
          ];
      return {
        ...prev,
        [draftActiveConcept]: { values: nextValues, allValues: false },
      };
    });
  };

  const isDraftValueChecked = (option: ConceptValue) =>
    !activeDraft.allValues &&
    activeDraft.values.some((item) => conceptValuesMatch(item, option));

  const dimensionSelectionCount = (conceptCode: string): number | undefined => {
    const entry = draftSelections[conceptCode];
    if (!entry || entry.allValues) return undefined;
    return entry.values.length > 0 ? entry.values.length : undefined;
  };

  const handleClearAll = () => {
    setDraftObjectType("");
    setDraftActiveConcept("");
    setDraftSelections({});
    setSearchInput("");
    setDimensionValueHits(new Set());
    setValueSearchBusy(false);
    activeProbeKeyRef.current = "";
    activeSelectedLoadKeyRef.current = "";
  };

  const handleConfirm = () => {
    const nextDimensions: ReferenceDimensionSelection[] = [];
    for (const concept of conceptOptions) {
      const entry = draftSelections[concept.code];
      if (!entry) continue;
      if (!entry.allValues && entry.values.length === 0) continue;
      nextDimensions.push({
        conceptCode: concept.code,
        conceptDisplayName: getConceptDisplayLabel(concept),
        values: entry.allValues ? [] : [...entry.values],
        allValues: entry.allValues,
      });
    }
    // Preserve hydrate-only dimensions not present in the current options list.
    for (const [code, entry] of Object.entries(draftSelections)) {
      if (nextDimensions.some((dim) => dim.conceptCode === code)) continue;
      if (!entry.allValues && entry.values.length === 0) continue;
      const committed = committedDimensions.find((dim) => dim.conceptCode === code);
      nextDimensions.push({
        conceptCode: code,
        conceptDisplayName: committed?.conceptDisplayName || code,
        values: entry.allValues ? [] : [...entry.values],
        allValues: entry.allValues,
      });
    }

    const legacy = legacyFieldsFromReferenceDimensions(nextDimensions);
    onChange({
      objectType: draftObjectType,
      ...legacy,
      referenceDimensions: nextDimensions,
      // Preserve Impacted selection when Reference is embedded in a combined draft.
      conceptCode: value.conceptCode,
      conceptValues: value.allValues ? [] : [...value.conceptValues],
      allValues: value.allValues,
    });
    handleClose();
  };

  // Concept V3: Object alone is enough to confirm; Dimension/Value are optional.
  const canConfirm = Boolean(draftObjectType.trim());

  // Three columns × 272px; keep popover wide enough so Object is not clipped.
  const popoverWidth = showDimensionAndValue ? 816 : 280;

  return (
    <>
      <CascadeTriggerField
        label={label}
        required
        displayValue={displayValue}
        placeholder={placeholder}
        open={open}
        error={error}
        helperText={helperText}
        disabled={disabled}
        onClick={handleOpen}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              width: showDimensionAndValue ? "max-content" : 280,
              minWidth: showDimensionAndValue ? popoverWidth : 280,
              maxWidth: "calc(100vw - 80px)",
              borderRadius: "6px",
              border: "none",
              boxShadow: "0 12px 32px rgba(25, 37, 50, 0.18)",
              overflow: "hidden",
              pt: 1.5,
              px: 1.75,
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: 220,
            maxWidth: "100%",
            border: "1px solid #E5E7EB",
            borderRadius: "3px",
            px: 1.25,
            py: "7px",
            boxSizing: "border-box",
            bgcolor: hasObject ? "transparent" : "#F9FAFB",
            opacity: hasObject ? 1 : 0.72,
          }}
        >
          <SearchOutlinedIcon sx={{ fontSize: 14, color: "#99A1AF", flexShrink: 0 }} />
          <Box
            component="input"
            value={searchInput}
            disabled={!hasObject}
            onChange={(event) => {
              if (!hasObject) return;
              setSearchInput((event.target as HTMLInputElement).value);
            }}
            placeholder="Input dimension or value..."
            aria-label="Search dimension or value"
            sx={{
              border: "none",
              outline: "none",
              boxShadow: "none",
              fontSize: "0.75rem",
              flex: 1,
              minWidth: 0,
              p: 0,
              fontFamily: "inherit",
              bgcolor: "transparent",
              color: hasObject ? "#0D0D0D" : "#99A1AF",
              cursor: hasObject ? "text" : "not-allowed",
              "&::placeholder": { color: "#99A1AF" },
              "&:disabled": {
                cursor: "not-allowed",
                WebkitTextFillColor: "#99A1AF",
              },
            }}
          />
        </Box>
        <Box
          sx={{
            borderTop: "1px solid #F0F1F3",
            mx: -1.75,
            mt: 1.5,
          }}
        />
        <Box
          sx={{
            display: "flex",
            minHeight: 260,
            overflow: "hidden",
            mx: -1.75,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              width: 272,
              flex: "none",
              boxSizing: "border-box",
              pt: 1.75,
              borderRight: showDimensionAndValue ? "1px solid #F0F1F3" : "none",
            }}
          >
            <Typography sx={cascadeColumnHeaderSx}>Object</Typography>
            <Box sx={{ ...cascadeColumnScrollSx, height: CASCADE_COLUMN_HEIGHT }}>
              {planTypesLoading && filteredObjects.length === 0 ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : filteredObjects.length === 0 ? (
                <Typography
                  sx={{ px: "18px", py: 1.5, fontSize: "0.75rem", color: "#99A1AF" }}
                >
                  No objects
                </Typography>
              ) : (
                filteredObjects.map((option) => (
                  <ColumnRow
                    key={option.code}
                    label={option.name}
                    selected={
                      draftObjectType.trim().toLowerCase() ===
                      option.code.trim().toLowerCase()
                    }
                    showCheck
                    onClick={() => handleSelectObject(option.code)}
                  />
                ))
              )}
            </Box>
          </Box>
          {showDimensionAndValue ? (
            <Box
              sx={{
                position: "relative",
                display: "flex",
                flex: "none",
                minHeight: 260,
                overflow: "hidden",
              }}
            >
              {showDimValueBodyLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 544,
                    height: CASCADE_COLUMN_HEIGHT + 37,
                    bgcolor: "#fff",
                  }}
                  aria-busy="true"
                  aria-label="Loading dimensions"
                >
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      minHeight: 0,
                      width: 272,
                      flex: "none",
                      boxSizing: "border-box",
                      pt: 1.75,
                      borderRight: "1px solid #F0F1F3",
                    }}
                  >
                    <Typography sx={cascadeColumnHeaderSx}>Dimension</Typography>
                    <Box
                      sx={{ ...cascadeColumnScrollSx, height: CASCADE_COLUMN_HEIGHT }}
                    >
                      {filteredDimensions.length === 0 ? (
                        <Typography
                          sx={{
                            px: "18px",
                            py: 1.5,
                            fontSize: "0.75rem",
                            color: "#99A1AF",
                          }}
                        >
                          {valueSearchBusy && search.trim()
                            ? "Searching…"
                            : "No dimensions"}
                        </Typography>
                      ) : (
                        filteredDimensions.map((option) => (
                          <ColumnRow
                            key={option.code}
                            label={getConceptDisplayLabel(option)}
                            selected={
                              draftActiveConcept.trim().toLowerCase() ===
                              option.code.trim().toLowerCase()
                            }
                            count={dimensionSelectionCount(option.code)}
                            onClick={() => handleSelectDimension(option.code)}
                          />
                        ))
                      )}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      minHeight: 0,
                      width: 272,
                      flex: "none",
                      boxSizing: "border-box",
                      pt: 1.75,
                    }}
                  >
                    <Typography sx={cascadeColumnHeaderSx}>Value</Typography>
                    <Box
                      ref={valuesScrollRef}
                      sx={{
                        ...cascadeColumnScrollSx,
                        height: CASCADE_COLUMN_HEIGHT,
                      }}
                      onScroll={(event) => {
                        const target = event.currentTarget;
                        if (!hasMoreNext || loadingMore) return;
                        const distanceFromBottom =
                          target.scrollHeight -
                          target.scrollTop -
                          target.clientHeight;
                        if (distanceFromBottom <= 40) {
                          void loadNext();
                        }
                      }}
                    >
                      {!draftActiveConcept ? (
                        <Typography
                          sx={{
                            px: "18px",
                            py: 1.5,
                            fontSize: "0.75rem",
                            color: "#99A1AF",
                          }}
                        >
                          Select a dimension
                        </Typography>
                      ) : valuesLoading ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            minHeight: CASCADE_COLUMN_HEIGHT - 8,
                            py: 2,
                          }}
                        >
                          <CircularProgress
                            size={24}
                            aria-label="Loading values"
                          />
                        </Box>
                      ) : mergedValueOptions.length === 0 ? (
                        <Typography
                          sx={{
                            px: "18px",
                            py: 1.5,
                            fontSize: "0.75rem",
                            color: "#99A1AF",
                          }}
                        >
                          No matching values
                        </Typography>
                      ) : (
                        <>
                          <ValueOptionRow
                            label={CONCEPT_VALUE_ALL_OPTION.name}
                            checked={activeDraft.allValues}
                            onClick={handleSelectAllValues}
                          />
                          {mergedValueOptions.map((option, index) => (
                            <ValueOptionRow
                              key={`${option.code || "value"}-${index}`}
                              label={option.name || option.code}
                              checked={isDraftValueChecked(option)}
                              onClick={() => handleToggleValue(option)}
                            />
                          ))}
                          {loadingMore === "next" ? (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                py: 1.5,
                              }}
                            >
                              <CircularProgress size={20} />
                            </Box>
                          ) : null}
                        </>
                      )}
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          ) : null}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderTop: "1px solid #F0F1F3",
            mx: -1.75,
            px: 3.5,
            py: 1.5,
          }}
        >
          <Button
            size="small"
            onClick={handleClearAll}
            startIcon={<FilterAltOffOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: "none",
              color: "#E7000B",
              fontWeight: 500,
              fontSize: "0.875rem",
              "&:hover": { bgcolor: "transparent", color: "#B00007" },
            }}
          >
            Clear all
          </Button>
          <IconButton
            aria-label="Confirm reference items"
            onClick={handleConfirm}
            disabled={!canConfirm}
            sx={{
              width: 40,
              height: 36,
              borderRadius: "3px",
              bgcolor: canConfirm ? primaryColor : "#E5E7EB",
              color: "#fff",
              "&:hover": {
                bgcolor: canConfirm ? primaryColor : "#E5E7EB",
              },
              "&.Mui-disabled": {
                bgcolor: "#E5E7EB",
                color: "#fff",
              },
            }}
          >
            <CheckIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Popover>
    </>
  );
}


type ImpactedItemsCascadePickerProps = Readonly<{
  value: ImpactedCascadeSelection;
  onChange: (next: ImpactedCascadeSelection) => void;
  /** Reference object type — dimensions are filtered by this type. */
  objectType?: string;
  /** Reference object subtype when present. */
  objectSubType?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}>;

export function ImpactedItemsCascadePicker({
  value,
  onChange,
  objectType = "",
  // Kept for call-site compatibility; dimensions are scoped by target_type only.
  objectSubType: _objectSubType = "",
  error,
  helperText,
  disabled,
}: ImpactedItemsCascadePickerProps) {
  const { primaryColor } = useBrandThemeColors();
  const trimmedObjectType = objectType.trim();
  const hasReferenceObject = Boolean(trimmedObjectType);
  /** Bumped on every popover open so Dimensions refetch for freshness. */
  const [dataReloadToken, setDataReloadToken] = useState(0);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  // Dimension list: GET configurable-concepts?target_type=…&kind=impacted
  // Load only while popover is open — not when the Add/Edit form mounts.
  const { conceptOptions, loading: conceptsLoading } = useConfigurableConcepts(
    hasReferenceObject ? trimmedObjectType : null,
    null,
    "impacted",
    { enabled: open, reloadToken: dataReloadToken },
  );
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedSearchQuery(searchInput);
  const [draftConceptCode, setDraftConceptCode] = useState(value.conceptCode);
  const [draftValues, setDraftValues] = useState<ConceptValue[]>(value.conceptValues);
  const [draftAllValues, setDraftAllValues] = useState(Boolean(value.allValues));
  /** Auto-select first Dimension once per open when nothing was pre-selected. */
  const didAutoSelectDimensionRef = useRef(false);

  const pickerDisabled = Boolean(disabled) || !hasReferenceObject;

  const {
    options: valueOptions,
    loading: valuesLoading,
    setInput: setValueSearchInput,
    loadOptions,
    loadNext,
    hasMoreNext,
    loadingMore,
  } = usePaginatedConceptValues(draftConceptCode, 20);
  const valuesScrollRef = useRef<HTMLDivElement | null>(null);
  /** Dimensions whose values matched the latest `name` search. */
  const [dimensionValueHits, setDimensionValueHits] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  /** True while multi-Dimension Value probe is in flight. */
  const [valueSearchBusy, setValueSearchBusy] = useState(false);
  const valueSearchRequestIdRef = useRef(0);
  /** Last full multi-Dimension probe key (`search|codes`). */
  const activeProbeKeyRef = useRef("");
  /** Last selected-Dimension list load key (`conceptCode|search`). */
  const activeSelectedLoadKeyRef = useRef("");

  const dimensionCodesKey = useMemo(
    () =>
      conceptOptions
        .map((item) => item.code.trim())
        .filter(Boolean)
        .join(","),
    [conceptOptions],
  );

  // Probe Value hits when the debounced search query changes (not per keystroke).
  // Intentionally omits draftConceptCode — switching Dimension must not re-probe all.
  useEffect(() => {
    if (!open) {
      valueSearchRequestIdRef.current += 1;
      activeProbeKeyRef.current = "";
      activeSelectedLoadKeyRef.current = "";
      setValueSearchBusy(false);
      setDimensionValueHits((prev) => (prev.size === 0 ? prev : new Set()));
      return;
    }

    const trimmed = search.trim();
    if (!trimmed) {
      valueSearchRequestIdRef.current += 1;
      activeProbeKeyRef.current = "";
      setValueSearchBusy(false);
      setDimensionValueHits((prev) => (prev.size === 0 ? prev : new Set()));
      return;
    }

    const dimensionCodes = dimensionCodesKey
      ? dimensionCodesKey.split(",")
      : [];
    if (dimensionCodes.length === 0) return;

    const probeKey = `${trimmed}|${dimensionCodes.join(",")}`;
    if (activeProbeKeyRef.current === probeKey) {
      setValueSearchBusy(false);
      return;
    }
    activeProbeKeyRef.current = probeKey;
    setDimensionValueHits((prev) => (prev.size === 0 ? prev : new Set()));
    setValueSearchBusy(true);
    const requestId = ++valueSearchRequestIdRef.current;

    void (async () => {
      try {
        const hitResults = await Promise.all(
          dimensionCodes.map(async (code) => {
            const response = await getConceptsValuesSearch(
              [code],
              trimmed || undefined,
              1,
              1,
            );
            const items = Array.isArray(response.data) ? response.data : [];
            return { code, hasHit: items.length > 0 };
          }),
        );

        if (requestId !== valueSearchRequestIdRef.current) return;

        const hits = new Set<string>();
        for (const result of hitResults) {
          if (result.hasHit) hits.add(result.code);
        }
        setDimensionValueHits(hits);
      } catch {
        if (requestId === valueSearchRequestIdRef.current) {
          setDimensionValueHits(new Set());
        }
      } finally {
        if (requestId === valueSearchRequestIdRef.current) {
          setValueSearchBusy(false);
        }
      }
    })();
  }, [dimensionCodesKey, open, search]);

  const searchLower = search.trim().toLowerCase();

  const dimensionMatchesSearchByName = useCallback(
    (conceptCode: string, queryLower: string): boolean => {
      if (!queryLower) return false;
      const option = conceptOptions.find((item) =>
        conceptCodesEqual(item.code, conceptCode),
      );
      if (!option) return false;
      const label = getConceptDisplayLabel(option).toLowerCase();
      return (
        label.includes(queryLower) ||
        option.code.toLowerCase().includes(queryLower)
      );
    },
    [conceptOptions],
  );

  /**
   * Value API `name` filter (same rules as Reference Items):
   * - Dimension name match only → load all Values for that Dimension
   * - Value hit (or both) → filter Values by the search text
   */
  const valueSearchNameForActive = useMemo(() => {
    const trimmed = search.trim();
    if (!trimmed || !draftConceptCode.trim()) return undefined;
    const nameMatch = dimensionMatchesSearchByName(
      draftConceptCode,
      trimmed.toLowerCase(),
    );
    const valueHit = dimensionHitSetHas(dimensionValueHits, draftConceptCode);
    if (nameMatch && !valueHit) return undefined;
    return trimmed;
  }, [
    dimensionMatchesSearchByName,
    dimensionValueHits,
    draftConceptCode,
    search,
  ]);

  // Dimension / search driven: load Values for the clicked Dimension only.
  useEffect(() => {
    if (!open || !draftConceptCode) return;

    const trimmedQuery = valueSearchNameForActive ?? "";
    const loadKey = `${draftConceptCode}|${trimmedQuery}`;
    if (activeSelectedLoadKeyRef.current === loadKey) return;
    activeSelectedLoadKeyRef.current = loadKey;

    setValueSearchInput(trimmedQuery);
    void loadOptions(trimmedQuery || undefined, { force: true });
  }, [
    draftConceptCode,
    loadOptions,
    open,
    setValueSearchInput,
    valueSearchNameForActive,
  ]);

  // Keep fetching while more pages exist and the list still cannot be scrolled,
  // or the user is already sitting at the bottom after an append.
  useEffect(() => {
    if (!open || !draftConceptCode || valuesLoading) return;
    if (!hasMoreNext || loadingMore) return;
    const node = valuesScrollRef.current;
    if (!node) return;
    const notScrollable = node.scrollHeight <= node.clientHeight + 8;
    const atBottom =
      node.scrollHeight - node.scrollTop - node.clientHeight <= 40;
    if (!notScrollable && !atBottom) return;
    void loadNext();
  }, [
    draftConceptCode,
    hasMoreNext,
    loadingMore,
    loadNext,
    open,
    valueOptions,
    valuesLoading,
  ]);

  // Prefer first Dimension when opening with an empty selection.
  useEffect(() => {
    if (!open || didAutoSelectDimensionRef.current) return;
    if (conceptsLoading || conceptOptions.length === 0) return;
    if (draftConceptCode.trim()) {
      didAutoSelectDimensionRef.current = true;
      return;
    }
    setDraftConceptCode(conceptOptions[0]!.code);
    didAutoSelectDimensionRef.current = true;
  }, [open, conceptsLoading, conceptOptions, draftConceptCode]);

  // Drop Impacted selection when it is invalid for the current Reference type.
  // Skip while options are still empty — hook starts as loading=false + [] and
  // would wipe edit hydrate before the fetch returns.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    if (!hasReferenceObject || conceptsLoading) return;
    if (!value.conceptCode.trim()) return;
    if (conceptOptions.length === 0) return;
    const selectedCode = value.conceptCode.trim().toLowerCase();
    const stillValid = conceptOptions.some(
      (item) => item.code.trim().toLowerCase() === selectedCode,
    );
    if (stillValid) return;
    onChangeRef.current({ conceptCode: "", conceptValues: [], allValues: false });
  }, [
    conceptOptions,
    conceptsLoading,
    hasReferenceObject,
    value.conceptCode,
  ]);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    if (pickerDisabled) return;
    const existingCode = value.conceptCode.trim();
    setDraftConceptCode(existingCode);
    setDraftValues(value.allValues ? [] : value.conceptValues);
    setDraftAllValues(Boolean(value.allValues));
    setSearchInput("");
    // Re-run auto-select only when opening without a saved Dimension.
    didAutoSelectDimensionRef.current = Boolean(existingCode);
    setDataReloadToken((token) => token + 1);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const dimensionMeta = conceptOptions.find(
    (item) =>
      item.code.trim().toLowerCase() === value.conceptCode.trim().toLowerCase(),
  );
  const displayValue = formatImpactedItemsLabel(
    dimensionMeta
      ? getConceptDisplayLabel(dimensionMeta)
      : value.conceptCode,
    value.allValues ? [] : value.conceptValues,
    Boolean(value.allValues),
  );

  // Pin draft selections at the top. While Values are API-filtered, only pin
  // selections that still match the query.
  const mergedValueOptions = useMemo(() => {
    if (draftAllValues) {
      return mergeValuesWithSelected(valueOptions, []);
    }
    const applySearch = Boolean(valueSearchNameForActive);
    const pinnedSelected = applySearch
      ? draftValues.filter((item) =>
          (item.name || item.code)
            .toLowerCase()
            .includes(valueSearchNameForActive!.toLowerCase()),
        )
      : draftValues;
    return mergeValuesWithSelected(valueOptions, pinnedSelected);
  }, [draftAllValues, draftValues, valueOptions, valueSearchNameForActive]);

  // Search Dimension name and Value: keep name matches and Value hits.
  const filteredConcepts = useMemo(() => {
    if (!searchLower) return conceptOptions;
    return conceptOptions.filter(
      (item) =>
        dimensionMatchesSearchByName(item.code, searchLower) ||
        dimensionHitSetHas(dimensionValueHits, item.code),
    );
  }, [
    conceptOptions,
    dimensionMatchesSearchByName,
    dimensionValueHits,
    searchLower,
  ]);

  // Prefer a name-matched Dimension immediately; otherwise a Value-hit Dimension.
  useEffect(() => {
    if (!open || !searchLower) return;
    if (conceptsLoading) return;
    if (filteredConcepts.length === 0) return;

    const activeMatchesName = dimensionMatchesSearchByName(
      draftConceptCode,
      searchLower,
    );
    const activeHasValueHit = dimensionHitSetHas(
      dimensionValueHits,
      draftConceptCode,
    );
    if (activeMatchesName || activeHasValueHit) return;

    const firstNameMatch = filteredConcepts.find((item) =>
      dimensionMatchesSearchByName(item.code, searchLower),
    );
    if (firstNameMatch) {
      setDraftConceptCode(firstNameMatch.code);
      setDraftValues([]);
      setDraftAllValues(false);
      return;
    }

    if (valueSearchBusy) return;
    const firstValueHit = filteredConcepts.find((item) =>
      dimensionHitSetHas(dimensionValueHits, item.code),
    );
    if (!firstValueHit) return;
    setDraftConceptCode(firstValueHit.code);
    setDraftValues([]);
    setDraftAllValues(false);
  }, [
    conceptsLoading,
    dimensionMatchesSearchByName,
    dimensionValueHits,
    draftConceptCode,
    filteredConcepts,
    open,
    searchLower,
    valueSearchBusy,
  ]);

  // Value `name` filter is applied by the API; keep full pages for scroll math.
  const filteredValues = mergedValueOptions;

  const showAllOption =
    Boolean(draftConceptCode) &&
    (!searchLower || CONCEPT_VALUE_ALL_OPTION.name.toLowerCase().includes(searchLower));

  const canConfirm =
    Boolean(draftConceptCode) &&
    (draftAllValues || draftValues.length > 0);

  const handleConfirm = () => {
    onChange({
      conceptCode: draftConceptCode,
      // Clone so parent draft rows never share the picker's draftValues ref.
      conceptValues: draftAllValues ? [] : [...draftValues],
      allValues: draftAllValues,
    });
    handleClose();
  };

  const handleToggleAll = () => {
    setDraftAllValues((prev) => !prev);
    setDraftValues([]);
  };

  const handleToggleValue = (option: ConceptValue) => {
    setDraftAllValues(false);
    setDraftValues((prev) => {
      const exists = prev.some((item) => conceptValuesMatch(item, option));
      if (exists) {
        return prev.filter((item) => !conceptValuesMatch(item, option));
      }
      // Drop any name/code-equivalent hydrate row, then store the list option.
      return [
        ...prev.filter((item) => !conceptValuesMatch(item, option)),
        option,
      ];
    });
  };

  const isDraftValueChecked = (option: ConceptValue) =>
    !draftAllValues &&
    draftValues.some((item) => conceptValuesMatch(item, option));

  return (
    <>
      <CascadeTriggerField
        label="Impacted Items"
        required
        displayValue={displayValue}
        placeholder={
          hasReferenceObject
            ? "Please select impacted Items"
            : "Please select reference Items first"
        }
        open={open}
        error={error}
        helperText={helperText}
        disabled={pickerDisabled}
        onClick={handleOpen}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              width: 506,
              maxWidth: "calc(100vw - 80px)",
              borderRadius: "6px",
              border: "none",
              boxShadow: "0 12px 32px rgba(25, 37, 50, 0.18)",
              overflow: "hidden",
              pt: 1.5,
              px: 1.75,
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: 220,
            maxWidth: "100%",
            border: "1px solid #E5E7EB",
            borderRadius: "3px",
            px: 1.25,
            py: "7px",
            boxSizing: "border-box",
          }}
        >
          <SearchOutlinedIcon sx={{ fontSize: 14, color: "#99A1AF", flexShrink: 0 }} />
          <Box
            component="input"
            value={searchInput}
            onChange={(event) =>
              setSearchInput((event.target as HTMLInputElement).value)
            }
            placeholder="Input dimension or value..."
            sx={{
              border: "none",
              outline: "none",
              boxShadow: "none",
              fontSize: "0.75rem",
              flex: 1,
              minWidth: 0,
              p: 0,
              fontFamily: "inherit",
              bgcolor: "transparent",
              color: "#0D0D0D",
              "&::placeholder": { color: "#99A1AF" },
            }}
          />
        </Box>
        <Box
          sx={{
            borderTop: "1px solid #F0F1F3",
            mx: -1.75,
            mt: 1.5,
          }}
        />
        <Box
          sx={{
            position: "relative",
            minHeight: 260,
            overflow: "hidden",
            mx: -1.75,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              minHeight: 260,
              overflow: "hidden",
            }}
          >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  borderRight: "1px solid #F0F1F3",
                  pt: 1.75,
                }}
              >
                <Typography sx={cascadeColumnHeaderSx}>Dimension</Typography>
                <Box sx={{ ...cascadeColumnScrollSx, height: CASCADE_COLUMN_HEIGHT }}>
                  {conceptsLoading ? (
                    <Typography sx={{ px: "18px", py: 1.5, fontSize: "0.75rem", color: "#99A1AF" }}>
                      Loading…
                    </Typography>
                  ) : filteredConcepts.length === 0 ? (
                    <Typography sx={{ px: "18px", py: 1.5, fontSize: "0.75rem", color: "#99A1AF" }}>
                      {conceptOptions.length === 0
                        ? "No dimensions for this object"
                        : valueSearchBusy
                          ? "Searching…"
                          : "No matching dimensions"}
                    </Typography>
                  ) : (
                    filteredConcepts.map((option) => {
                      const isDraftDimension = draftConceptCode === option.code;
                      // Discrete Value picks only — All has no finite count badge.
                      const dimensionValueCount =
                        isDraftDimension && !draftAllValues
                          ? draftValues.length
                          : 0;
                      return (
                        <ColumnRow
                          key={option.code}
                          label={getConceptDisplayLabel(option)}
                          selected={isDraftDimension}
                          count={dimensionValueCount}
                          onClick={() => {
                            if (option.code === draftConceptCode) return;
                            setDraftConceptCode(option.code);
                            const isCommittedDimension =
                              normalizeConceptValueCode(option.code) ===
                              normalizeConceptValueCode(value.conceptCode);
                            if (isCommittedDimension) {
                              // Restore saved Values when returning to the committed Dimension.
                              setDraftValues(
                                value.allValues ? [] : value.conceptValues,
                              );
                              setDraftAllValues(Boolean(value.allValues));
                            } else {
                              setDraftValues([]);
                              setDraftAllValues(false);
                            }
                          }}
                        />
                      );
                    })
                  )}
                </Box>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, pt: 1.75 }}>
                <Typography sx={cascadeColumnHeaderSx}>Value</Typography>
                <Box
                  ref={valuesScrollRef}
                  sx={{ ...cascadeColumnScrollSx, height: CASCADE_COLUMN_HEIGHT }}
                  onScroll={(event) => {
                    const target = event.currentTarget;
                    if (!hasMoreNext || loadingMore) return;
                    const distanceFromBottom =
                      target.scrollHeight - target.scrollTop - target.clientHeight;
                    if (distanceFromBottom <= 40) {
                      void loadNext();
                    }
                  }}
                >
                  {!draftConceptCode ? (
                    <Typography sx={{ px: "18px", py: 1.5, fontSize: "0.75rem", color: "#99A1AF" }}>
                      Select a dimension
                    </Typography>
                  ) : valuesLoading ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        minHeight: CASCADE_COLUMN_HEIGHT - 8,
                        py: 2,
                      }}
                    >
                      <CircularProgress size={24} aria-label="Loading values" />
                    </Box>
                  ) : (
                    <>
                      {showAllOption ? (
                        <ValueOptionRow
                          label={CONCEPT_VALUE_ALL_OPTION.name}
                          checked={draftAllValues}
                          onClick={handleToggleAll}
                        />
                      ) : null}
                      {filteredValues.map((option, index) => {
                        const optionKey = normalizeConceptValueCode(option.code);
                        const checked = isDraftValueChecked(option);
                        return (
                          <ValueOptionRow
                            key={`${optionKey || "value"}-${index}`}
                            label={option.name || option.code}
                            checked={checked}
                            onClick={() => handleToggleValue(option)}
                          />
                        );
                      })}
                      {loadingMore === "next" ? (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 1.5,
                          }}
                        >
                          <CircularProgress
                            size={20}
                            aria-label="Loading more values"
                          />
                        </Box>
                      ) : null}
                    </>
                  )}
                </Box>
              </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderTop: "1px solid #F0F1F3",
            mx: -1.75,
            px: 3.5,
            py: 1.5,
          }}
        >
          <Button
            size="small"
            onClick={() => {
              // Clear Values only — keep Dimension so options stay loaded.
              setDraftValues([]);
              setDraftAllValues(false);
              setSearchInput("");
            }}
            startIcon={<FilterAltOffOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: "none",
              color: "#E7000B",
              fontWeight: 500,
              fontSize: "0.875rem",
              "&:hover": { bgcolor: "transparent", color: "#B00007" },
            }}
          >
            Clear all
          </Button>
          <IconButton
            aria-label="Confirm impacted items"
            onClick={handleConfirm}
            disabled={!canConfirm}
            sx={{
              width: 40,
              height: 36,
              borderRadius: "3px",
              bgcolor: canConfirm ? primaryColor : "#E5E7EB",
              color: "#fff",
              "&:hover": {
                bgcolor: canConfirm ? primaryColor : "#E5E7EB",
              },
              "&.Mui-disabled": { bgcolor: "#E5E7EB", color: "#fff" },
            }}
          >
            <CheckIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Popover>
    </>
  );
}
